//src/factory/symbols/createLightSwichSymbols.js
import * as THREE from 'three';
import { generateUniqueId } from '../../utils/generateUniqueId';

export function createLightSwich(
  scene,
  mousePos,
  dir = 1,
  color = 0x0045ff,
  isPreview = false,
  type = "normal",
  tempLineUUID,
  rotateValue = 0,
  providedLightSwitchId, // optional: if provided, use this id instead of generating a new one
  stretchLength = 0 // optional: stretch amount applied to line1 and relative positions
) {
  const group = new THREE.Group();
  const offsetGroup = new THREE.Group();
  // offsetGroup'u tanımlamak için userData ekle
  offsetGroup.userData = { isOffsetGroup: true, stretchLength: stretchLength };
  dir = dir ?? 1;
  const width = 14;
  const height = 28;
  const rotationGizmoColor = isPreview ? 0x3333ff : 0x3333ff;
  const strechGizmoColor = isPreview ? 0x33ff33 : 0x33ff33;
  const symetricGizmoColor = isPreview ? 0xff3aa3 : 0xff3aa3;
  const moveGizmoColor = isPreview ? 0xff3223 : 0x00ffff;
  const innerRadius = 4; // Gizmo boyutunu büyüttük (2'den 4'e)
  const basePoint = { x: mousePos.x, y: mousePos.y };
  const LightSwichId = providedLightSwitchId || generateUniqueId('LightSwitch');
  // rotateValue'yu normalize et (undefined veya null ise 0)
  const normalizedRotate = typeof rotateValue === 'number' ? rotateValue : 0;
  // stretchLength'i normalize et
  const normalizedStretch = typeof stretchLength === 'number' ? stretchLength : 0;

  group.userData = {
    id: LightSwichId,
    type: 'LightSwitch',
    subtype: type,
    isSelectable: true,
    isPreview: isPreview,
    connectedTo: null,
    basePoint,
    tempLineUUID,
    power: 1,
    rotate: normalizedRotate,
    stretchLength: normalizedStretch,
    originalColor: color,
  };
  group.position.set(basePoint.x, basePoint.y, 0);
  // Use provided color when available so preview matches settings-derived color.
  // Fall back to legacy preview blue or non-preview cyan when color is not provided.
  const lineColor = (typeof color !== 'undefined' && color !== null)
    ? color
    : (isPreview ? 0x0022bb : 0x00ffff);

  const lineMaterial = new THREE.LineBasicMaterial({
    color: lineColor,
    transparent: isPreview,
    opacity: isPreview ? 0.5 : 1
  });

  // ✅ line1: base length 15 + stretchLength
  const line1Length = 15 + normalizedStretch;
  const line1Geo = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(0, line1Length, 0),
  ]);

  const line1 = new THREE.Line(line1Geo, lineMaterial);
  line1.userData = { originalColor: lineMaterial.color.getHex(), role: 'LS_line1' }; // LightSwichLine1
  offsetGroup.add(line1);

  if (type.includes('normal') || type === 'etanj') {
    // ✅ line2: starts at circle top (27 + stretch), ends at 42 + stretch
    const line2Geo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 27 + normalizedStretch, 0),
      new THREE.Vector3(0, 42 + normalizedStretch, 0),
      new THREE.Vector3(5, 42 + normalizedStretch, 0),
    ]);
    const line2 = new THREE.Line(line2Geo, lineMaterial);
    line2.userData = { originalColor: lineMaterial.color.getHex(), role: 'LS_line2' };
    offsetGroup.add(line2);
  }
  if (type.toLowerCase().includes('komutatör') || type.toLowerCase().includes('komütatör')) {
    // ✅ Komütatör: line2 and line3 positions adjusted by stretchLength
    const line2Geo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(2.92, 26.6 + normalizedStretch, 0),
      new THREE.Vector3(10.7, 39.11 + normalizedStretch, 0),
      new THREE.Vector3(15.02, 36.58 + normalizedStretch, 0),
    ]);
    const line2 = new THREE.Line(line2Geo, lineMaterial);
    line2.userData = { originalColor: lineMaterial.color.getHex(), role: 'LS_line2' };
    offsetGroup.add(line2);

    const line3Geo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(-2.92, 26.6 + normalizedStretch, 0),
      new THREE.Vector3(-10.7, 39.11 + normalizedStretch, 0),
      new THREE.Vector3(-15.02, 36.58 + normalizedStretch, 0),
    ]);
    const line3 = new THREE.Line(line3Geo, lineMaterial);
    line3.userData = { originalColor: lineMaterial.color.getHex(), role: 'LS_line3' };
    offsetGroup.add(line3);
  }
  if (type.toLowerCase().includes('vaviyen')) {
    // ✅ Vaviyen: line2 and line3 positions adjusted by stretchLength
    const line2Geo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(2.92, 26.6 + normalizedStretch, 0),
      new THREE.Vector3(10.7, 39.11 + normalizedStretch, 0),
      new THREE.Vector3(15.02, 36.58 + normalizedStretch, 0),
    ]);
    const line2 = new THREE.Line(line2Geo, lineMaterial);
    line2.userData = { originalColor: lineMaterial.color.getHex(), role: 'LS_line2' };
    offsetGroup.add(line2);

    const line3Geo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(-2.92, 26.6 + normalizedStretch, 0),
      new THREE.Vector3(-10.7, 39.11 + normalizedStretch, 0),
      new THREE.Vector3(-6.07, 41.69 + normalizedStretch, 0),
    ]);
    const line3 = new THREE.Line(line3Geo, lineMaterial);
    line3.userData = { originalColor: lineMaterial.color.getHex(), role: 'LS_line3' };
    offsetGroup.add(line3);
  }

  // ✅ Etanj fill: position adjusted by stretchLength
  if (type.includes('etanj')) {
    const etanjShape = new THREE.Shape();
    etanjShape.moveTo(0, 0);
    etanjShape.absarc(0, 0, 6, 0, Math.PI, false);
    etanjShape.lineTo(0, 0);
    const geometry = new THREE.ShapeGeometry(etanjShape);
    const material = new THREE.MeshBasicMaterial({ color: lineMaterial.color.getHex(), side: THREE.DoubleSide });
    const etanjFill = new THREE.Mesh(geometry, material);
    etanjFill.position.set(0, 21 + normalizedStretch, 0);
    etanjFill.rotation.z = Math.PI;
    etanjFill.userData = { role: 'etanjFill' };
    offsetGroup.add(etanjFill);
  }


  const createFilledTriangle = (p1, p2, p3, color) => {
    const shape = new THREE.Shape();
    shape.moveTo(p1.x, p1.y);
    shape.lineTo(p2.x, p2.y);
    shape.lineTo(p3.x, p3.y);
    shape.lineTo(p1.x, p1.y); // üçgeni kapat

    const geometry = new THREE.ShapeGeometry(shape);
    const material = new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide });
    return new THREE.Mesh(geometry, material);
  };
  const createFilledTetragon = (p1, p2, p3, p4, color) => {
    const shape = new THREE.Shape();
    shape.moveTo(p1.x, p1.y);
    shape.lineTo(p2.x, p2.y);
    shape.lineTo(p3.x, p3.y);
    shape.lineTo(p4.x, p4.y);
    shape.lineTo(p1.x, p1.y); // üçgeni kapat

    const geometry = new THREE.ShapeGeometry(shape);
    const material = new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide });
    return new THREE.Mesh(geometry, material);
  };
  const createFilledCircle = (radius, color) => {
    const geometry = new THREE.CircleGeometry(radius, 64);
    const material = new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide });
    return new THREE.Mesh(geometry, material);
  };


  // ✅ Stretch gizmo - positioned at line1 end (base y=15 + stretchLength)
  // Note: this creates the initial geometry at a fixed position, but we'll reposition it below
  const strechGizmoP1 = new THREE.Vector3(10, 0, 0);
  const strechGizmoP2 = new THREE.Vector3(7, 3, 0);
  const strechGizmoP3 = new THREE.Vector3(4, 0, 0);
  const strechGizmo = createFilledTriangle(strechGizmoP1, strechGizmoP2, strechGizmoP3, strechGizmoColor);
  // Position stretch gizmo at circle position (which is stretchLength offset from base)
  strechGizmo.position.set(0, normalizedStretch, 0);

  // ✅ Symmetric gizmo - positioned at circle location
  const symetricGizmoP1 = new THREE.Vector3(-10, 0, 0);
  const symetricGizmoP2 = new THREE.Vector3(-7, 3, 0);
  const symetricGizmoP3 = new THREE.Vector3(-4, 0, 0);
  const symetricGizmoP4 = new THREE.Vector3(-7, -3, 0);
  const symetrical = createFilledTetragon(symetricGizmoP1, symetricGizmoP2, symetricGizmoP3, symetricGizmoP4, symetricGizmoColor);
  symetrical.position.set(0, normalizedStretch, 0);

  // ⚠️ MOVE GIZMO ŞİMDİLİK İPTAL (geçici olarak devre dışı)
  /*
  const moveGizmoP1 = new THREE.Vector3(7, 7, 0);
  const moveGizmoP2 = new THREE.Vector3(7, 13, 0);
  const moveGizmoP3 = new THREE.Vector3(13, 13, 0);
  const moveGizmoP4 = new THREE.Vector3(13, 7, 0);

  const moveGizmo = createFilledTetragon(moveGizmoP1, moveGizmoP2, moveGizmoP3, moveGizmoP4, moveGizmoColor)
  */

  const innerCircle = createFilledCircle(innerRadius, rotationGizmoColor);
  innerCircle.userData = {
    type: 'rotateGizmo',
    parentLightSwitchId: LightSwichId,
  };
  symetrical.userData = {
    type: 'symetricGizmo',
    parentLightSwitchId: LightSwichId,
  };
  strechGizmo.userData = {
    type: 'stretchGizmo',
    parentLightSwitchId: LightSwichId,
  };
  /*
  moveGizmo.userData = {
    type: 'moveGizmo',
    parentLightSwitchId: LightSwichId,
  };
  */
  // ✅ Rotate gizmo - positioned at top of symbol (height + 5 + stretchLength)
  innerCircle.position.set(width / 2 - 7, height + 5 + normalizedStretch, 0.2);



  innerCircle.visible = false
  strechGizmo.visible = false
  symetrical.visible = false
  // moveGizmo.visible = false  // ⚠️ İPTAL


  offsetGroup.add(innerCircle);
  offsetGroup.add(strechGizmo);
  offsetGroup.add(symetrical);
  // offsetGroup.add(moveGizmo);  // ⚠️ İPTAL


  // ✅ Circle: center position adjusted by stretchLength (base y=21 + stretchLength)
  const circleRadius = 6;
  const circleCenter = new THREE.Vector3(0, 21 + normalizedStretch, 0);
  const circlePoints = [];
  const segments = 32;

  for (let i = 0; i <= segments; i++) {
    const angle = (i / segments) * Math.PI * 2;
    const x = circleCenter.x + circleRadius * Math.cos(angle);
    const y = circleCenter.y + circleRadius * Math.sin(angle);
    circlePoints.push(new THREE.Vector3(x, y, 0));
  }

  const circleGeometry = new THREE.BufferGeometry().setFromPoints(circlePoints);
  const circleLine = new THREE.LineLoop(circleGeometry, lineMaterial);
  circleLine.userData = { originalColor: lineMaterial.color.getHex(), role: 'LS_circle' };
  offsetGroup.add(circleLine);
  //--------------------------------------------------------------------

  // 🔧 Offset uygulaması (merkez uca oturtulacak)
  // dir may be a numeric flip (-1 or 1) or a THREE.Vector3 direction.
  const yScale = (typeof dir === 'number') ? dir : 1;
  offsetGroup.scale.set(1, yScale, 1);
  offsetGroup.position.set(0, 0, 0);

  // If a direction vector is passed, orient the whole group to that direction
  // and apply the user rotate offset on the inner offsetGroup (so parent rotation = direction,
  // offsetGroup.rotation = user rotate offset in radians). This mirrors the socket symbol behavior
  // and prevents mouse-move recalculations from wiping out the user's rotate offset.
  const rotateOffsetRadians = normalizedRotate * Math.PI / 180;
  if (dir instanceof THREE.Vector3) {
    const angle = Math.atan2(dir.y, dir.x); // Radians
    group.rotation.z = angle;
    offsetGroup.rotation.z = rotateOffsetRadians;
  } else {
    // no direction vector: apply only the user rotate offset on offsetGroup
    offsetGroup.rotation.z = rotateOffsetRadians;
  }

  group.add(offsetGroup);

  if (scene && !isPreview) {
    scene.add(group);
  }

  return group;
}
 
export function singleCircleBuatForLight(
  scene,
  point,
  color = 0x0045ff,
  uuid
) {
  
    const geometry = new THREE.CircleGeometry(2, 64);
    const material = new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide }); 
    const buatCircle = new THREE.Mesh(geometry, material);  
    buatCircle.position.set(point.x, point.y, 0.2);
    buatCircle.name="lightingBuat"
  buatCircle.userData = {
    type: 'buatLightingCircle', //buatLightingCircle
    LightSwichUUID: uuid,
    snapType: 'lightingBuat',
  };

  return  buatCircle
 

}
export function singleSquareBuat(
  scene,
  point,
  color = 0x0045ff,
  uuid
) { 
  const square = new THREE.Mesh(
    new THREE.PlaneGeometry(5, 5),
    new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide })
  );
  square.position.set(point.x, point.y, 0.2);
  square.name="lightingBuat"
  square.userData = {
    type: 'buatLightingSquare',
    LightSwichUUID: uuid,
    snapType: 'lightingBuat',

  };
  return  square


}

 